import React, {
  forwardRef, useEffect, useImperativeHandle, useRef, useState,
} from 'react';

// command-autocomplete slug (Phase 2): a small dropdown of `/` commands for the
// chat input, driven by the station's command catalog
// (GET /api/stations/{id}/commands, built in Phase 1). Shows while the user is
// typing a command NAME (starts with `/`, no space/paren yet); picking one
// inserts a ready-to-fill command and shows the signature as a hint.
//
// Keyboard nav (self-review #2): ↑/↓ move the highlight, Enter picks the
// highlighted command, Esc dismisses. The parent textarea delegates keydowns to
// `ref.handleKeyDown(e)` FIRST — it returns true when it consumed the key, so
// Enter picks a suggestion instead of sending (and only when the dropdown is up).
//
// Degrades to nothing on empty/failed fetch — never blocks sending.

// stationId -> commands[]. Only SUCCESSFUL fetches are cached (self-review #1):
// a transient failure is NOT cached, so the next command-start retries instead
// of being disabled for the whole session.
const _cache = {};

async function fetchCatalog(stationId) {
  if (stationId == null) return [];
  if (_cache[stationId] !== undefined) return _cache[stationId];
  try {
    const r = await fetch(`/api/stations/${stationId}/commands`, { credentials: 'same-origin' });
    if (r.ok) {
      _cache[stationId] = (await r.json()).commands || [];
      return _cache[stationId];
    }
  } catch (_e) { /* fall through — do NOT cache a failure */ }
  return [];   // transient: uncached → a later command-start refetches
}

// Full signature hint, e.g. `/Menu.reserve(date, party_size)`.
function signature(c) {
  const params = (c.params || []).map((p) => p.name).join(', ');
  return `/${c.qualified}(${params})`;
}

// Ready-to-fill insert: alias → `/verb ` (bareword takes rest-of-line);
// qualified → `/Set.func(` (open for args) or `/Set.func()` when it takes none.
function insertText(c) {
  if (c.alias) return `/${c.alias} `;
  return (c.params || []).length ? `/${c.qualified}(` : `/${c.qualified}()`;
}

// Matches shown ONLY while typing the command NAME — once there's a space or
// `(`, the user is filling args, so we get out of the way.
function matchesFor(catalog, value) {
  const v = value || '';
  if (!v.startsWith('/') || /[\s(]/.test(v)) return [];
  const q = v.slice(1).toLowerCase();
  return catalog.filter((c) => (
    (c.alias && c.alias.toLowerCase().startsWith(q))
    || c.qualified.toLowerCase().startsWith(q)
    || c.qualified.toLowerCase().includes(`.${q}`)
  )).slice(0, 8);
}

const CommandAutocomplete = forwardRef(function CommandAutocomplete(
  { stationId, value, onPick }, ref,
) {
  const [catalog, setCatalog] = useState([]);
  const [sel, setSel] = useState(0);
  const [dismissed, setDismissed] = useState(false);
  const [showDetail, setShowDetail] = useState(false);

  // Lazy fetch: only when the user actually starts a command (and retry on each
  // fresh command-start, since failures aren't cached).
  const isCmd = (value || '').startsWith('/');
  useEffect(() => {
    if (!isCmd) return undefined;
    let live = true;
    fetchCatalog(stationId).then((c) => { if (live) setCatalog(c); });
    return () => { live = false; };
  }, [stationId, isCmd]);

  // Reset the highlight + un-dismiss whenever the query changes.
  useEffect(() => { setSel(0); setDismissed(false); }, [value]);

  // Reveal a function's details (signature + description) only after the
  // highlight has rested on it for a moment — keeps the list compact while
  // arrowing/hovering through, details on pause.
  useEffect(() => {
    setShowDetail(false);
    const t = setTimeout(() => setShowDetail(true), 500);
    return () => clearTimeout(t);
  }, [sel, value]);

  const matches = dismissed ? [] : matchesFor(catalog, value);

  // Refs so the parent-invoked handleKeyDown reads the current matches/sel.
  const matchesRef = useRef(matches); matchesRef.current = matches;
  const selRef = useRef(sel); selRef.current = sel;

  useImperativeHandle(ref, () => ({
    // Returns true iff the key was consumed (parent should NOT also send).
    handleKeyDown(e) {
      const m = matchesRef.current;
      if (!m.length) return false;
      if (e.key === 'ArrowDown') { e.preventDefault(); setSel((s) => Math.min(s + 1, m.length - 1)); return true; }
      if (e.key === 'ArrowUp') { e.preventDefault(); setSel((s) => Math.max(s - 1, 0)); return true; }
      if (e.key === 'Enter') {
        e.preventDefault();
        onPick(insertText(m[Math.min(selRef.current, m.length - 1)]));
        return true;
      }
      if (e.key === 'Escape') { e.preventDefault(); setDismissed(true); return true; }
      return false;
    },
  }));

  if (!matches.length) return null;
  return (
    <div style={dropdownStyle} role="listbox" aria-label="command suggestions">
      {matches.map((c, i) => (
        <div
          key={c.qualified}
          role="option"
          aria-selected={i === sel}
          style={{ ...itemStyle, background: i === sel ? '#eef4ff' : '#fff' }}
          onMouseEnter={() => setSel(i)}
          // onMouseDown (not onClick) + preventDefault so the textarea keeps
          // focus and doesn't blur before the pick registers.
          onMouseDown={(e) => { e.preventDefault(); onPick(insertText(c)); }}
        >
          <code style={{ fontWeight: 600 }}>{c.alias ? `/${c.alias}` : `/${c.qualified}`}</code>
          {i === sel && showDetail && (
            <>
              <span style={{ color: '#999', marginLeft: 8, fontSize: '0.78rem' }}>{signature(c)}</span>
              {c.description
                ? <div style={{ color: '#666', fontSize: '0.76rem', marginTop: 2 }}>{c.description}</div>
                : null}
            </>
          )}
        </div>
      ))}
    </div>
  );
});

export default CommandAutocomplete;

const dropdownStyle = {
  position: 'absolute', bottom: '100%', left: 0, right: 0, marginBottom: 6,
  background: '#fff', border: '1px solid #ddd', borderRadius: 8,
  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)', maxHeight: 220, overflowY: 'auto',
  zIndex: 50,
};
const itemStyle = {
  padding: '6px 12px', cursor: 'pointer', borderBottom: '1px solid #f0f0f0',
};
